/**
 * Proyecta el registro de recursos del contratista a la porción de UNA OT.
 *
 * Es la costura entre el módulo nuevo y el reporte diario: el contratista
 * declara una vez, y de aquí sale lo que le corresponde a cada OT en la forma
 * que el reporte ya sabe leer (`Recursos`).
 *
 * # Por qué produce la forma antigua
 *
 * Porque los catorce lectores de `rd.recursos` —`calcularKpis`, el PDF firmado,
 * el tablero, las curvas— no tienen por qué enterarse. El registro es una
 * superficie de captura; el reporte sigue siendo el registro contable del día.
 *
 * # La cantidad viaja repartida, las horas y los km NO
 *
 * `horas` y `km` son por unidad. Si además se repartieran, el consumo se
 * dividiría dos veces y el diésel de la OT saldría muchísimo menor que el real.
 * Se reparte la cantidad; cada unidad conserva su jornada y su recorrido.
 */

import type {
  LineaRecurso,
  RegistroRecursos,
  Recursos,
  RecursoMaquinaria,
  RecursoPersonal,
  RecursoVehiculo,
  RecursoHerramienta,
} from "@app-types/entities";
import type { ClasePersonal, OrigenPersonal } from "@constants/catalogos";
import {
  CAT_MAQ_AMARILLA,
  CAT_VEH_MOVILIZACION,
  claseDe,
} from "@features/so/rdShared";

/** Cuánto de esta línea le tocó a la OT. Cero si no participa. */
function cantidadDe(linea: LineaRecurso, soId: string): number {
  return linea.reparto?.[soId] ?? 0;
}

/**
 * Recursos que le corresponden a una OT según el registro del día.
 *
 * Las líneas con porción cero se omiten: incluirlas llenaría el inventario del
 * tablero de filas vacías, que es justo lo que se corrigió al filtrar el
 * catálogo.
 *
 * La maquinaria y los vehículos se reparten entre sus dos sub-listas por
 * catálogo —amarilla vs herramientas, movilización vs carga— porque de esa
 * clasificación depende la tabla de rendimiento con la que se deriva el diésel.
 * Un equipo fuera de catálogo cae en la segunda lista, que es donde su
 * rendimiento vale cero: preferible un cero explicable a un consumo inventado.
 */
export function porcionDeOt(registro: RegistroRecursos | null, soId: string): Recursos {
  const vacio: Recursos = {
    maqAmarilla: [],
    maqHerramientas: [],
    vehMovilizacion: [],
    vehCarga: [],
    personal: [],
    herramientas: [],
  };
  if (!registro) return vacio;

  const maqAmarilla: RecursoMaquinaria[] = [];
  const maqHerramientas: RecursoMaquinaria[] = [];
  for (const l of registro.maquinaria ?? []) {
    const cantidad = cantidadDe(l, soId);
    if (cantidad <= 0) continue;
    const fila: RecursoMaquinaria = {
      equipo: l.nombre,
      cantidad,
      horas: l.horas ?? 0,
      // El galón se deriva aguas abajo; aquí no se declara.
      galones: 0,
    };
    (CAT_MAQ_AMARILLA.includes(l.nombre) ? maqAmarilla : maqHerramientas).push(fila);
  }

  const vehMovilizacion: RecursoVehiculo[] = [];
  const vehCarga: RecursoVehiculo[] = [];
  for (const l of registro.vehiculos ?? []) {
    const cantidad = cantidadDe(l, soId);
    if (cantidad <= 0) continue;
    const fila: RecursoVehiculo = {
      equipo: l.nombre,
      cantidad,
      km: l.km ?? 0,
      galones: 0,
    };
    (CAT_VEH_MOVILIZACION.includes(l.nombre) ? vehMovilizacion : vehCarga).push(fila);
  }

  const personal: RecursoPersonal[] = [];
  for (const l of registro.personal ?? []) {
    const cantidad = cantidadDe(l, soId);
    if (cantidad <= 0) continue;
    const clase: ClasePersonal = l.clase ?? claseDe(l.nombre);
    const origen: OrigenPersonal = l.origen ?? (clase === "STAFF" ? "Foraneo" : "Local");
    personal.push({ rol: l.nombre, origen, clase, cantidad });
  }

  // Las herramientas menores no consumen diésel ni entran en las estadísticas
  // del tablero, pero el reporte diario de cada OT sí las lista. Devolverlas
  // vacías dejaba al reporte sin herramientas en cuanto leía del registro.
  const herramientas: RecursoHerramienta[] = [];
  for (const l of registro.herramientas ?? []) {
    const cantidad = cantidadDe(l, soId);
    if (cantidad <= 0) continue;
    herramientas.push({ herramienta: l.nombre, cantidad });
  }

  return {
    maqAmarilla,
    maqHerramientas,
    vehMovilizacion,
    vehCarga,
    personal,
    herramientas,
  };
}

/** ¿Trae algo? Sirve para no pisar los recursos ya cargados con un vacío. */
export function porcionVacia(r: Recursos): boolean {
  return (
    r.maqAmarilla.length === 0 &&
    r.maqHerramientas.length === 0 &&
    r.vehMovilizacion.length === 0 &&
    r.vehCarga.length === 0 &&
    r.personal.length === 0 &&
    r.herramientas.length === 0
  );
}
